import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "../context/AuthContext";

/**
 * <AiDescriptionButtons />
 *
 * Two-button block for AI-assisted business description writing (Feature #2,
 * Feb 25 2026). Renders under a description textarea.
 *
 * Props:
 *   value:     current textarea value (string)
 *   onChange:  (newText: string) => void
 *   summaryHint: optional short summary shown next to Generate button
 *
 * Rules (enforced server-side in AI_DESCRIPTION_SYSTEM_PROMPT):
 *   - Never invents certifications, awards, licenses, years of experience,
 *     guarantees, or customer reviews.
 *   - Only expands on facts already given.
 */
export default function AiDescriptionButtons({ value, onChange, testidPrefix = "ai-desc" }) {
  const { authHeader } = useAuth();
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);

  const generateFromSummary = async () => {
    const s = summary.trim();
    if (s.length < 10) { toast.error("Give at least 10 characters of summary to generate from."); return; }
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/business/description/generate`,
        { summary: s },
        { headers: authHeader() },
      );
      const text = r.data?.description?.trim();
      if (text) {
        onChange(text);
        toast.success("Generated — feel free to edit before saving");
        setSummaryOpen(false);
        setSummary("");
      } else {
        toast.error("AI didn't return a description. Try again.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't generate description");
    } finally { setBusy(false); }
  };

  const improveCurrent = async () => {
    const s = (value || "").trim();
    if (s.length < 20) { toast.error("Write at least 20 characters first, then Improve."); return; }
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/business/description/improve`,
        { current_description: s },
        { headers: authHeader() },
      );
      const text = r.data?.description?.trim();
      if (text) {
        onChange(text);
        toast.success("Improved — feel free to keep editing");
      } else {
        toast.error("AI didn't return an improved description");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't improve description");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-3" data-testid={`${testidPrefix}-block`}>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setSummaryOpen((v) => !v)}
          disabled={busy}
          data-testid={`${testidPrefix}-generate-toggle`}
          className="text-xs uppercase tracking-[0.15em] rounded-full border px-4 py-2 transition-colors disabled:opacity-50"
          style={{ borderColor: "var(--gf-accent)", color: "var(--gf-accent)", background: "transparent" }}
        >
          {summaryOpen ? "Cancel" : "\u2728 Generate Description with AI"}
        </button>
        <button
          type="button"
          onClick={improveCurrent}
          disabled={busy || (value || "").trim().length < 20}
          data-testid={`${testidPrefix}-improve`}
          className="text-xs uppercase tracking-[0.15em] rounded-full border px-4 py-2 transition-colors disabled:opacity-50"
          style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text-soft)", background: "transparent" }}
        >
          {busy ? "Thinking\u2026" : "\u2728 Improve Description with AI"}
        </button>
      </div>

      {summaryOpen && (
        <div className="mt-3 rounded-2xl border p-4" style={{ borderColor: "var(--gf-border-strong)", background: "var(--gf-surface-2)" }} data-testid={`${testidPrefix}-summary-panel`}>
          <label className="text-xs uppercase tracking-[0.15em]" style={{ color: "var(--gf-text-muted)" }}>
            Short summary (25\u201350 words)
          </label>
          <p className="text-xs mt-1 mb-2" style={{ color: "var(--gf-text-muted)" }}>
            Example: "We provide mobile car detailing throughout Atlanta specializing in interior cleaning and ceramic coatings."
          </p>
          <textarea
            data-testid={`${testidPrefix}-summary-input`}
            rows={3}
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="Tell us what your business does, where, and any specialties..."
            className="w-full bg-transparent border rounded-md px-3 py-2 outline-none focus:border-[var(--gf-accent)]"
            style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
          />
          <div className="mt-3 flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={generateFromSummary}
              disabled={busy || summary.trim().length < 10}
              data-testid={`${testidPrefix}-generate-submit`}
              className="text-xs rounded-full px-4 py-2 disabled:opacity-50"
              style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
            >
              {busy ? "Generating\u2026" : "Generate"}
            </button>
            <p className="text-[11px] flex-1" style={{ color: "var(--gf-text-muted)" }}>
              The AI never invents certifications, awards, licenses, years of experience, guarantees, or customer reviews \u2014 it only writes about facts you provide.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
